import React, { useEffect, useRef, useState } from 'react';
import { resolveDocTitle, subjectNamesFromStandards } from '../../utils/evaluationPlanDocTitle';
import AutoStatusToast from '../common/AutoStatusToast';
import type { EvaluationPlanAttachment, EvaluationPlanAttachmentSlot } from '../../types/evaluationPlan';
import { EVALUATION_PLAN_ATTACHMENT_SLOT_LABELS } from '../../types/evaluationPlan';
import type { EvaluationLevelDisplayLabels, EvaluationLevelStepCount, EvaluationPlanDraft, EvaluationStandardDraft } from '../../types/evaluationPlan';
import {
  addWebDocumentColumnAfter,
  addWebDocumentMetaRowAfter,
  applyEvaluationPlanToWebDocument,
  buildWebDocumentFromTemplateStructure,
  cloneEvaluationPlanWebDocumentData,
  columnHasContent,
  createDefaultEvaluationPlanWebDocument,
  extractWebDocumentTemplateStructure,
  formatWebDocumentAcademicYearLabel,
  getWebDocumentColumnInsertAfterId,
  getWebDocumentCriterionColumnId,
  getWebDocumentMetaElementRowId,
  getWebDocumentMetaRowInsertAfterId,
  isWebDocumentSchoolYearMetaRow,
  metaRowHasContent,
  parseEvaluationPlanWebDocumentData,
  removeWebDocumentColumn,
  removeWebDocumentMetaRow,
  removeWebDocumentRow,
  rubricRowHasContent,
  syncWebDocumentLevelRowLabels,
  webDocumentHasWhiteFieldContent,
  webDocumentPlanApplyTargetHasContent,
  webDocumentTemplateStructureDirty,
  WEB_DOCUMENT_DEFAULT_TEMPLATE_ID,
  type EvaluationPlanWebDocumentData,
  DEFAULT_EXAMPLE_ANSWER_TITLE,
  DEFAULT_CRITERION_COLUMN_HEADER,
  DEFAULT_GUBUN_COLUMN_HEADER,
  DEFAULT_NOTICE_LABEL_LINES,
  getWebDocumentDualLabelLines,
  isWebDocumentGubunColumn,
} from '../../types/evaluationPlanWebDocument';
import { useAutoStatusToast } from '../../hooks/useAutoStatusToast';
import {
  type EvaluationPlanWebDocumentTemplate,
} from '../../utils/evaluationPlanWebDocumentTemplateStorage';
import {
  deleteEvaluationWebDocumentTemplate,
  listEvaluationWebDocumentTemplates,
  renameEvaluationWebDocumentTemplate,
  upsertEvaluationWebDocumentTemplate,
} from '../../services/evaluationWebDocumentTemplateService';
import { useEvaluationPlanWebDocumentA4Layout } from '../../utils/evaluationPlanWebDocumentA4Layout';
import {
  holdModalSavingLabelMinDuration,
  paintModalSaveInProgress,
} from '../../utils/modalSaveButtonIndicator';
import EvaluationPlanDeleteHoldButton from './EvaluationPlanDeleteHoldButton';
import WebDocHoverLabel from './WebDocHoverLabel';
import UnsavedChangesConfirmModal from '../common/UnsavedChangesConfirmModal';
import EvaluationPlanWebDocumentTemplateNameModal from './EvaluationPlanWebDocumentTemplateNameModal';
import EvaluationPlanWebDocumentTemplateTabs from './EvaluationPlanWebDocumentTemplateTabs';
import WebDocAutoGrowTextarea from './WebDocAutoGrowTextarea';

type PendingDelete =
  | { kind: 'meta_row'; id: string; label: string }
  | { kind: 'rubric_row'; id: string; label: string }
  | { kind: 'column'; id: string; label: string }
  | { kind: 'template'; template: EvaluationPlanWebDocumentTemplate };

type TemplateNameModalState =
  | { mode: 'save' }
  | { mode: 'rename'; template: EvaluationPlanWebDocumentTemplate };

const META_COLGROUP = (
  <colgroup>
    <col className="cp-evaluation-plan-web-doc-col-label" />
    <col className="cp-evaluation-plan-web-doc-col-value" />
    <col className="cp-evaluation-plan-web-doc-col-label" />
    <col className="cp-evaluation-plan-web-doc-col-value" />
  </colgroup>
);

const WEB_DOC_ROW_H1 = 'cp-evaluation-plan-web-doc-row-h-1';
const WEB_DOC_ROW_H2 = 'cp-evaluation-plan-web-doc-row-h-2';

const EvaluationPlanWebDocumentEditorModal: React.FC<{
  open: boolean;
  slot: EvaluationPlanAttachmentSlot | null;
  existingAttachment?: EvaluationPlanAttachment | null;
  planSource?: EvaluationPlanDraft | null;
  standards?: EvaluationStandardDraft[];
  levelStepCount?: EvaluationLevelStepCount;
  levelDisplayLabels?: EvaluationLevelDisplayLabels | null;
  onClose: () => void;
  onSave: (slot: EvaluationPlanAttachmentSlot, attachment: EvaluationPlanAttachment) => void | Promise<void>;
}> = ({
  open,
  slot,
  existingAttachment = null,
  planSource = null,
  standards = [],
  levelStepCount = 5,
  levelDisplayLabels,
  onClose,
  onSave,
}) => {
  /** 제목 자동: 평가계획에 고른 성취기준의 과목명 */
  const subjectNames = subjectNamesFromStandards(
    (planSource?.standard_ids ?? [])
      .map((id) => standards.find((st) => st.id === id))
      .filter((st): st is EvaluationStandardDraft => !!st)
  );
  const [draft, setDraft] = useState<EvaluationPlanWebDocumentData | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const [saveCompleted, setSaveCompleted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<PendingDelete | null>(null);
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);
  const [templates, setTemplates] = useState<EvaluationPlanWebDocumentTemplate[]>([]);
  const [activeTemplateId, setActiveTemplateId] = useState(WEB_DOCUMENT_DEFAULT_TEMPLATE_ID);
  const [templateNameModal, setTemplateNameModal] = useState<TemplateNameModalState | null>(null);
  const [templateNameError, setTemplateNameError] = useState<string | null>(null);
  const [pendingTemplateSwitch, setPendingTemplateSwitch] = useState<string | null>(null);
  const [pendingPlanApply, setPendingPlanApply] = useState(false);
  const { toastState, showSuccess, showError } = useAutoStatusToast(3000);
  const pageRef = useRef<HTMLDivElement>(null);
  const wasOpenRef = useRef(false);

  const markDirty = () => {
    setIsDirty(true);
    setSaveCompleted(false);
  };

  const exampleAnswerContentForLayout = (() => {
    const current = draft?.exampleAnswer ?? { example: '', answer: '' };
    if (current.example && current.answer) {
      return `${current.example}\n${current.answer}`;
    }
    return current.example || current.answer || '';
  })();

  useEvaluationPlanWebDocumentA4Layout(pageRef, [
    draft?.metaRows,
    draft?.rows,
    draft?.columns,
    draft?.title,
    exampleAnswerContentForLayout,
    open,
  ]);

  const refreshTemplates = async () => {
    if (!slot) return;
    const forSlot = slot;
    try {
      const list = await listEvaluationWebDocumentTemplates(forSlot);
      setTemplates(list);
    } catch {
      /* 목록 실패 시 기존 표시 유지 */
    }
  };

  useEffect(() => {
    if (!open || !slot) {
      if (!open) {
        wasOpenRef.current = false;
        setDraft(null);
        setCloseConfirmOpen(false);
        setIsDirty(false);
        setSaveCompleted(false);
        setSaving(false);
      }
      setPendingDelete(null);
      setTemplates([]);
      setActiveTemplateId(WEB_DOCUMENT_DEFAULT_TEMPLATE_ID);
      setTemplateNameModal(null);
      setTemplateNameError(null);
      setPendingTemplateSwitch(null);
      setPendingPlanApply(false);
      return;
    }

    const justOpened = !wasOpenRef.current;
    wasOpenRef.current = true;
    if (!justOpened) return;

    void refreshTemplates();
    const parsed = parseEvaluationPlanWebDocumentData(existingAttachment?.form_data?.web_document);
    const base = parsed ?? createDefaultEvaluationPlanWebDocument(slot, levelStepCount, levelDisplayLabels);
    const synced = syncWebDocumentLevelRowLabels(base, levelStepCount, levelDisplayLabels);
    setDraft({ ...synced, title: resolveDocTitle(slot === 'criterion' ? 'criterion' : 'sheet', synced.title, subjectNames) });
    setActiveTemplateId(WEB_DOCUMENT_DEFAULT_TEMPLATE_ID);
    setIsDirty(false);
    setSaveCompleted(false);
    setPendingDelete(null);
    setTemplateNameModal(null);
    setTemplateNameError(null);
    setPendingTemplateSwitch(null);
    setPendingPlanApply(false);
  }, [open, slot, existingAttachment, levelStepCount, levelDisplayLabels]);

  if (!open || !slot || !draft) return null;

  const slotLabel = EVALUATION_PLAN_ATTACHMENT_SLOT_LABELS[slot];
  const criterionColumnId = getWebDocumentCriterionColumnId(draft);
  const metaElementRowId = getWebDocumentMetaElementRowId(draft);
  const activeTemplate =
    activeTemplateId === WEB_DOCUMENT_DEFAULT_TEMPLATE_ID
      ? null
      : templates.find((template) => template.id === activeTemplateId) ?? null;
  const templateBaselineStructure =
    activeTemplate?.structure ?? extractWebDocumentTemplateStructure(
      createDefaultEvaluationPlanWebDocument(slot, levelStepCount, levelDisplayLabels)
    );
  const canSaveTemplate = webDocumentTemplateStructureDirty(draft, templateBaselineStructure);
  const visibleMetaRows = draft.metaRows.filter((row) => !isWebDocumentSchoolYearMetaRow(row) && !row.hidden);
  /** 숨김(삭제)된 '평가 시 유의사항' 행 — 다시 추가 버튼용 */
  const hiddenNoticeRow = draft.metaRows.find((row) => row.layout === 'dual_label' && row.hidden) ?? null;
  const displayAcademicYearLabel = formatWebDocumentAcademicYearLabel();

  const patchDraft = (patch: Partial<EvaluationPlanWebDocumentData>) => {
    markDirty();
    setDraft((prev) => (prev ? { ...prev, ...patch } : prev));
  };

  const patchMetaRow = (rowId: string, patch: Partial<EvaluationPlanWebDocumentData['metaRows'][number]>) => {
    markDirty();
    setDraft((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        metaRows: prev.metaRows.map((row) => (row.id === rowId ? { ...row, ...patch } : row)),
      };
    });
  };

  const patchColumnHeader = (columnId: string, header: string) => {
    markDirty();
    setDraft((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        columns: prev.columns.map((col) => (col.id === columnId ? { ...col, header } : col)),
      };
    });
  };

  const patchRowLabel = (rowId: string, label: string) => {
    markDirty();
    setDraft((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        rows: prev.rows.map((row) => (row.id === rowId ? { ...row, label } : row)),
      };
    });
  };

  const patchRowMemo = (rowId: string, memo: string) => {
    markDirty();
    setDraft((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        rows: prev.rows.map((row) => (row.id === rowId ? { ...row, memo } : row)),
      };
    });
  };

  const patchCell = (rowId: string, columnId: string, value: string) => {
    markDirty();
    setDraft((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        rows: prev.rows.map((row) =>
          row.id === rowId ? { ...row, cells: { ...row.cells, [columnId]: value } } : row
        ),
      };
    });
  };

  const patchExampleAnswerContent = (value: string) => {
    markDirty();
    setDraft((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        exampleAnswer: { ...prev.exampleAnswer, example: value, answer: '' },
      };
    });
  };

  const patchExampleAnswerTitle = (value: string) => {
    markDirty();
    setDraft((prev) => {
      if (!prev) return prev;
      return { ...prev, exampleAnswer: { ...prev.exampleAnswer, title: value } };
    });
  };

  const exampleAnswerContent = (() => {
    const current = draft.exampleAnswer ?? { example: '', answer: '' };
    if (current.example && current.answer) {
      return `${current.example}\n${current.answer}`;
    }
    return current.example || current.answer || '';
  })();

  const requestDelete = (target: Exclude<PendingDelete, { kind: 'template' }>) => {
    if (!draft) return;

    const hasContent =
      target.kind === 'meta_row'
        ? metaRowHasContent(draft.metaRows.find((row) => row.id === target.id)!)
        : target.kind === 'rubric_row'
          ? rubricRowHasContent(draft, draft.rows.find((row) => row.id === target.id)!)
          : columnHasContent(draft, target.id);

    if (hasContent) {
      setPendingDelete(target);
      return;
    }
    applyDelete(target);
  };

  const applyDelete = (target: Exclude<PendingDelete, { kind: 'template' }>) => {
    markDirty();
    setDraft((prev) => {
      if (!prev) return prev;
      if (target.kind === 'meta_row') return removeWebDocumentMetaRow(prev, target.id);
      if (target.kind === 'rubric_row') return removeWebDocumentRow(prev, target.id);
      return removeWebDocumentColumn(prev, target.id);
    });
    setPendingDelete(null);
  };

  const handleSave = async (): Promise<boolean> => {
    if (saving || !isDirty || !draft) return !isDirty;
    let ok = false;
    const saveStartedAt = Date.now();
    setSaving(true);
    await paintModalSaveInProgress();
    try {
      const savedDraft = cloneEvaluationPlanWebDocumentData(draft);
      await onSave(slot, {
        kind: 'web_document',
        slot,
        registered_at: existingAttachment?.registered_at ?? new Date().toISOString(),
        preview_label: slotLabel,
        form_data: { web_document: savedDraft },
      });
      setDraft(savedDraft);
      setIsDirty(false);
      setSaveCompleted(true);
      ok = true;
    } catch (error) {
      console.error('웹 문서 저장 실패:', error);
      alert('기준안 등록에 실패했습니다.');
    } finally {
      await holdModalSavingLabelMinDuration(saveStartedAt);
      setSaving(false);
    }
    return ok;
  };

  /** 닫기·바깥 클릭·Esc: 변경사항이 있으면 저장 확인 */
  const requestClose = () => {
    if (saving) return;
    if (isDirty) {
      setCloseConfirmOpen(true);
      return;
    }
    onClose();
  };

  const applyTemplate = (templateId: string) => {
    if (!slot) return;
    markDirty();
    if (templateId === WEB_DOCUMENT_DEFAULT_TEMPLATE_ID) {
      setDraft(createDefaultEvaluationPlanWebDocument(slot, levelStepCount, levelDisplayLabels));
      setActiveTemplateId(WEB_DOCUMENT_DEFAULT_TEMPLATE_ID);
      return;
    }

    const template = templates.find((item) => item.id === templateId);
    if (!template) return;
    setDraft(
      syncWebDocumentLevelRowLabels(
        buildWebDocumentFromTemplateStructure(slot, template.structure),
        levelStepCount,
        levelDisplayLabels
      )
    );
    setActiveTemplateId(templateId);
  };

  const requestTemplateSwitch = (templateId: string) => {
    if (templateId === activeTemplateId) return;
    if (webDocumentHasWhiteFieldContent(draft)) {
      setPendingTemplateSwitch(templateId);
      return;
    }
    applyTemplate(templateId);
  };

  const getSaveTemplateInitialName = () => {
    if (activeTemplateId === WEB_DOCUMENT_DEFAULT_TEMPLATE_ID) return '';
    return templates.find((template) => template.id === activeTemplateId)?.name ?? '';
  };

  const applyPlanData = () => {
    if (!planSource || !draft) return;
    markDirty();
    setDraft(applyEvaluationPlanToWebDocument(draft, planSource, standards, levelStepCount, levelDisplayLabels));
    showSuccess('평가계획 데이터를 반영했습니다.');
  };

  const requestApplyPlanData = () => {
    if (!planSource || !draft) return;
    if (webDocumentPlanApplyTargetHasContent(draft, levelStepCount, levelDisplayLabels)) {
      setPendingPlanApply(true);
      return;
    }
    applyPlanData();
  };

  const handleSaveTemplate = async (name: string) => {
    if (!slot || !draft) return;
    const trimmed = name.trim();
    const isUpdate = templates.some((template) => template.name === trimmed);
    try {
      const saved = await upsertEvaluationWebDocumentTemplate(slot, name, extractWebDocumentTemplateStructure(draft));
      await refreshTemplates();
      setActiveTemplateId(saved.id);
      setTemplateNameModal(null);
      setTemplateNameError(null);
      showSuccess(
        isUpdate
          ? `「${saved.name}」 양식이 수정되었습니다.`
          : `「${saved.name}」 양식이 저장되었습니다.`
      );
    } catch (error) {
      setTemplateNameError(error instanceof Error ? error.message : '양식을 저장하지 못했습니다.');
    }
  };

  const handleRenameTemplate = async (name: string) => {
    if (!slot || templateNameModal?.mode !== 'rename') return;
    try {
      await renameEvaluationWebDocumentTemplate(slot, templateNameModal.template.id, name);
      await refreshTemplates();
      setTemplateNameModal(null);
      setTemplateNameError(null);
    } catch (error) {
      setTemplateNameError(error instanceof Error ? error.message : '양식 이름을 수정하지 못했습니다.');
    }
  };

  const confirmDeleteTemplate = async () => {
    if (!slot || pendingDelete?.kind !== 'template') return;
    try {
      await deleteEvaluationWebDocumentTemplate(slot, pendingDelete.template.id);
    } catch (error) {
      showError(error instanceof Error ? error.message : '양식을 삭제하지 못했습니다.');
      setPendingDelete(null);
      return;
    }
    if (activeTemplateId === pendingDelete.template.id) {
      setDraft(createDefaultEvaluationPlanWebDocument(slot, levelStepCount, levelDisplayLabels));
      setActiveTemplateId(WEB_DOCUMENT_DEFAULT_TEMPLATE_ID);
    }
    await refreshTemplates();
    setPendingDelete(null);
  };

  const renderAddButton = (onAdd: () => void, label: string) => (
    <button
      type="button"
      className="cp-evaluation-plan-web-doc-circle-add-btn"
      data-tutorial-ep="web-doc-circle-add"
      title={label}
      aria-label={label}
      onClick={onAdd}
    >
      +
    </button>
  );

  const renderMetaLabelCell = (
    row: EvaluationPlanWebDocumentData['metaRows'][number],
    content: React.ReactNode
  ) => {
    const showAdd = row.id === metaElementRowId;

    if (showAdd) {
      return (
        <th className="cp-evaluation-plan-web-doc-label-cell">
          <WebDocHoverLabel
            value={row.label}
            editable={false}
            actions={renderAddButton(() => {
              markDirty();
              setDraft((prev) => {
                if (!prev) return prev;
                const afterId = getWebDocumentMetaRowInsertAfterId(prev);
                return afterId ? addWebDocumentMetaRowAfter(prev, afterId) : prev;
              });
            }, '행 추가')}
          />
        </th>
      );
    }

    if (row.isUserAdded) {
      return (
        <th className="cp-evaluation-plan-web-doc-label-cell">
          <WebDocHoverLabel
            value={row.label}
            fallbackText="항목명"
            placeholder="항목명"
            ariaLabel="항목명"
            onCommit={(v) => patchMetaRow(row.id, { label: v })}
            actions={
              <EvaluationPlanDeleteHoldButton
                itemId={row.id}
                className="cp-evaluation-plan-web-doc-delete-hold-btn cp-wd-hover-btn"
                onRemove={() => requestDelete({ kind: 'meta_row', id: row.id, label: row.label || '추가 행' })}
              />
            }
          />
        </th>
      );
    }

    return (
      <th className="cp-evaluation-plan-web-doc-label-cell">
        <div className="cp-evaluation-plan-web-doc-title-cell-inner cp-evaluation-plan-web-doc-meta-label-plain">
          {content}
        </div>
      </th>
    );
  };

  const renderColumnHeader = (column: EvaluationPlanWebDocumentData['columns'][number]) => {
    const isGuBun = isWebDocumentGubunColumn(column);
    const isCriterion = column.id === criterionColumnId;
    const showCriterionAdd = isCriterion;

    return (
      <th
        key={column.id}
        className={`cp-evaluation-plan-web-doc-head-cell${isGuBun ? ' cp-evaluation-plan-web-doc-gubun-col' : ''}${column.isUserAdded ? ' is-user-col' : ''}`}
      >
        <WebDocHoverLabel
          value={column.header}
          fallbackText={isGuBun ? DEFAULT_GUBUN_COLUMN_HEADER : isCriterion ? DEFAULT_CRITERION_COLUMN_HEADER : '열 제목'}
          placeholder={isGuBun ? DEFAULT_GUBUN_COLUMN_HEADER : isCriterion ? DEFAULT_CRITERION_COLUMN_HEADER : '열 제목'}
          editable={isGuBun || isCriterion || !!column.isUserAdded}
          ariaLabel={isGuBun ? '채점 기준 열 제목' : isCriterion ? '평가 항목 열 제목' : '열 제목'}
          onCommit={(v) => patchColumnHeader(column.id, v)}
          actions={
            column.isUserAdded ? (
              <EvaluationPlanDeleteHoldButton
                itemId={column.id}
                className="cp-evaluation-plan-web-doc-delete-hold-btn cp-wd-hover-btn"
                onRemove={() => requestDelete({ kind: 'column', id: column.id, label: column.header || '추가 열' })}
              />
            ) : showCriterionAdd ? (
              renderAddButton(() => {
                markDirty();
                setDraft((prev) => {
                  if (!prev) return prev;
                  const afterId = getWebDocumentColumnInsertAfterId(prev);
                  return afterId ? addWebDocumentColumnAfter(prev, afterId) : prev;
                });
              }, '열 추가')
            ) : null
          }
        />
      </th>
    );
  };

  return (
    <>
      <div
        className="cp-modal-backdrop cp-evaluation-plan-web-doc-backdrop"
        role="presentation"
        onKeyDown={(e) => {
          if (e.key === 'Escape' && !pendingDelete && !templateNameModal && !pendingTemplateSwitch && !pendingPlanApply) requestClose();
        }}
        onMouseDown={(e) => {
          if (e.target === e.currentTarget && !pendingDelete && !templateNameModal && !pendingTemplateSwitch && !pendingPlanApply) requestClose();
        }}
      >
        <div
          className="cp-modal cp-evaluation-plan-web-doc-modal cp-evaluation-plan-web-doc-editor-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cp-evaluation-plan-web-doc-title"
          data-tutorial-ep="web-doc-modal"
          data-tutorial-ep-slot={slot ?? undefined}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="cp-evaluation-plan-web-doc-modal-head">
            <div className="cp-evaluation-plan-web-doc-modal-head-row">
              <h3 id="cp-evaluation-plan-web-doc-title">{slotLabel} 웹 문서 작성</h3>
            </div>
          </div>

          {slot === 'criterion' ? (
            <div className="cp-evaluation-plan-web-doc-template-toolbar" data-tutorial-ep="web-doc-templates">
              <EvaluationPlanWebDocumentTemplateTabs
                templates={templates}
                activeTemplateId={activeTemplateId}
                onSelect={requestTemplateSwitch}
                onRename={(template) => {
                  setTemplateNameError(null);
                  setTemplateNameModal({ mode: 'rename', template });
                }}
                onDelete={(template) => {
                  setPendingDelete({ kind: 'template', template });
                }}
              />
              <div className="cp-evaluation-plan-web-doc-template-toolbar-actions">
                <button
                  type="button"
                  className="cp-btn-secondary cp-evaluation-plan-web-doc-template-save-btn"
                  data-tutorial-ep="web-doc-save-template"
                  disabled={!canSaveTemplate}
                  title={canSaveTemplate ? undefined : '양식 구조에 변경 사항이 있을 때 저장할 수 있습니다.'}
                  onClick={() => {
                    if (!canSaveTemplate) return;
                    setTemplateNameError(null);
                    setTemplateNameModal({ mode: 'save' });
                  }}
                >
                  기준안 양식 저장
                </button>
                <button
                  type="button"
                  className="cp-btn-secondary cp-evaluation-plan-web-doc-template-apply-btn"
                  data-tutorial-ep="web-doc-apply-plan"
                  disabled={!planSource}
                  title={planSource ? undefined : '평가계획 수정 화면에서만 사용할 수 있습니다.'}
                  onClick={requestApplyPlanData}
                >
                  평가계획 데이터 반영
                </button>
              </div>
            </div>
          ) : null}

          <div className="cp-evaluation-plan-web-doc-full-bleed-line" aria-hidden="true" />

          <div className="cp-modal-body cp-evaluation-plan-web-doc-body">
            <div className="cp-evaluation-plan-web-doc-page-scroll">
              <div ref={pageRef} className="cp-evaluation-plan-web-doc-page">
                <div className="cp-evaluation-plan-web-doc-title-row">
                  <div className="cp-evaluation-plan-web-doc-title-badge">
                    <input
                      type="text"
                      className="cp-evaluation-plan-web-doc-title-input"
                      value={draft.title}
                      onChange={(e) => patchDraft({ title: e.target.value })}
                      aria-label="문서 제목"
                    />
                  </div>
                </div>

                <p className="cp-evaluation-plan-web-doc-school-year-line">{displayAcademicYearLabel}</p>

                <div className="cp-evaluation-plan-web-doc-page-main" data-tutorial-ep="web-doc-table">
                <table className="cp-evaluation-plan-web-doc-table cp-evaluation-plan-web-doc-meta-table">
                  {META_COLGROUP}
                  <tbody>
                    {visibleMetaRows.map((row) => {
                      if (row.layout === 'pair') {
                        return (
                          <tr key={row.id} className={WEB_DOC_ROW_H1}>
                            <th className="cp-evaluation-plan-web-doc-label-cell">
                              <span className="cp-evaluation-plan-web-doc-head-label">{row.label}</span>
                            </th>
                            <td className="cp-evaluation-plan-web-doc-value-cell">
                              <WebDocAutoGrowTextarea
                                className="cp-evaluation-plan-web-doc-cell-textarea"
                                value={row.value}
                                onChange={(value) => patchMetaRow(row.id, { value })}
                                ariaLabel={`${row.label} 내용`}
                              />
                            </td>
                            <th className="cp-evaluation-plan-web-doc-label-cell">
                              <span className="cp-evaluation-plan-web-doc-head-label">{row.pairLabel ?? ''}</span>
                            </th>
                            <td className="cp-evaluation-plan-web-doc-value-cell">
                              <WebDocAutoGrowTextarea
                                className="cp-evaluation-plan-web-doc-cell-textarea"
                                value={row.pairValue ?? ''}
                                onChange={(value) => patchMetaRow(row.id, { pairValue: value })}
                                ariaLabel={`${row.pairLabel ?? ''} 내용`}
                              />
                            </td>
                          </tr>
                        );
                      }

                      if (row.layout === 'dual_label') {
                        const labelText = (row.labelLines ?? DEFAULT_NOTICE_LABEL_LINES).join('\n');
                        return (
                          <tr key={row.id} className={`${WEB_DOC_ROW_H2} cp-wd-notice-row`}>
                            <th className="cp-evaluation-plan-web-doc-label-cell">
                              <WebDocHoverLabel
                                value={labelText}
                                fallbackText={DEFAULT_NOTICE_LABEL_LINES.join('\n')}
                                placeholder={DEFAULT_NOTICE_LABEL_LINES.join('\n')}
                                multiline
                                ariaLabel="평가 시 유의사항 제목"
                                onCommit={(v) => patchMetaRow(row.id, { labelLines: v.split('\n') })}
                                actions={
                                  <EvaluationPlanDeleteHoldButton
                                    itemId={row.id}
                                    className="cp-evaluation-plan-web-doc-delete-hold-btn cp-wd-hover-btn"
                                    onRemove={() => patchMetaRow(row.id, { hidden: true })}
                                  />
                                }
                              />
                            </th>
                            <td className="cp-evaluation-plan-web-doc-value-cell" colSpan={3}>
                              <WebDocAutoGrowTextarea
                                className="cp-evaluation-plan-web-doc-cell-textarea"
                                value={row.value}
                                onChange={(value) => patchMetaRow(row.id, { value })}
                                ariaLabel="평가 시·유의사항 내용"
                              />
                            </td>
                          </tr>
                        );
                      }

                      if (!row.label && !row.isUserAdded) {
                        return null;
                      }

                      return (
                        <tr key={row.id} className={WEB_DOC_ROW_H1}>
                          {renderMetaLabelCell(
                            row,
                            <span className="cp-evaluation-plan-web-doc-head-label">{row.label}</span>
                          )}
                          <td className="cp-evaluation-plan-web-doc-value-cell" colSpan={3}>
                            <WebDocAutoGrowTextarea
                              className="cp-evaluation-plan-web-doc-cell-textarea"
                              value={row.value}
                              onChange={(value) => patchMetaRow(row.id, { value })}
                              ariaLabel={`${row.label} 내용`}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {hiddenNoticeRow ? (
                  <button
                    type="button"
                    className="cp-evaluation-plan-web-doc-restore-row-btn"
                    onClick={() => patchMetaRow(hiddenNoticeRow.id, { hidden: false })}
                  >
                    + {getWebDocumentDualLabelLines(hiddenNoticeRow).join(' ')}
                  </button>
                ) : null}

                <div className="cp-evaluation-plan-web-doc-example-answer-section">
                  <div className="cp-evaluation-plan-web-doc-example-answer-header">
                    <WebDocHoverLabel
                      value={draft.exampleAnswer?.title ?? ''}
                      fallbackText={DEFAULT_EXAMPLE_ANSWER_TITLE}
                      placeholder={DEFAULT_EXAMPLE_ANSWER_TITLE}
                      ariaLabel="평가 과제 제목"
                      onCommit={patchExampleAnswerTitle}
                    />
                  </div>
                  <div className="cp-evaluation-plan-web-doc-example-answer-body">
                    <WebDocAutoGrowTextarea
                      className="cp-evaluation-plan-web-doc-cell-textarea"
                      shellClassName="cp-evaluation-plan-web-doc-example-answer-input-shell"
                      value={exampleAnswerContent}
                      onChange={patchExampleAnswerContent}
                      fillAvailableHeight
                      ariaLabel="예시 답안 내용"
                    />
                  </div>
                </div>

                <table className="cp-evaluation-plan-web-doc-table cp-evaluation-plan-web-doc-rubric-table">
                  <colgroup>
                    <col className="cp-evaluation-plan-web-doc-col-label" />
                    {draft.columns.slice(1).map((column) => (
                      <col key={column.id} className="cp-evaluation-plan-web-doc-col-rubric-data" />
                    ))}
                  </colgroup>
                  <thead>
                    <tr className="cp-evaluation-plan-web-doc-rubric-head-row">{draft.columns.map((column) => renderColumnHeader(column))}</tr>
                  </thead>
                  <tbody>
                    {draft.rows.map((row) => (
                      <tr key={row.id} className={row.isUserAdded ? WEB_DOC_ROW_H1 : WEB_DOC_ROW_H2}>
                        <th
                          className="cp-evaluation-plan-web-doc-row-label-cell cp-evaluation-plan-web-doc-gubun-col"
                          data-tutorial-ep="web-doc-gubun-memo-cell"
                        >
                          <div className="cp-evaluation-plan-web-doc-gubun-cell-inner">
                            {row.isUserAdded ? (
                              <WebDocHoverLabel
                                value={row.label}
                                fallbackText="행 제목"
                                placeholder="행 제목"
                                ariaLabel="행 제목"
                                onCommit={(v) => patchRowLabel(row.id, v)}
                                actions={
                                  <EvaluationPlanDeleteHoldButton
                                    itemId={row.id}
                                    className="cp-evaluation-plan-web-doc-delete-hold-btn cp-wd-hover-btn"
                                    onRemove={() =>
                                      requestDelete({
                                        kind: 'rubric_row',
                                        id: row.id,
                                        label: row.label || '추가 행',
                                      })
                                    }
                                  />
                                }
                              />
                            ) : (
                              <>
                                <span className="cp-evaluation-plan-web-doc-row-label">{row.label}</span>
                                <WebDocAutoGrowTextarea
                                  className="cp-evaluation-plan-web-doc-cell-textarea cp-evaluation-plan-web-doc-gubun-memo"
                                  value={row.memo ?? ''}
                                  onChange={(value) => patchRowMemo(row.id, value)}
                                  placeholder="기준 메모"
                                  ariaLabel={`${row.label} 메모`}
                                />
                              </>
                            )}
                          </div>
                        </th>
                        {draft.columns
                          .filter((col) => !isWebDocumentGubunColumn(col))
                          .map((column) => (
                            <td key={`${row.id}-${column.id}`} className="cp-evaluation-plan-web-doc-data-cell">
                              <WebDocAutoGrowTextarea
                                className="cp-evaluation-plan-web-doc-cell-textarea"
                                value={row.cells[column.id] ?? ''}
                                onChange={(value) => patchCell(row.id, column.id, value)}
                                ariaLabel={`${row.label} ${column.header}`}
                              />
                            </td>
                          ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              </div>
            </div>
          </div>

          <div className="cp-evaluation-plan-web-doc-full-bleed-line" aria-hidden="true" />

          <div className="cp-modal-footer cp-evaluation-plan-web-doc-modal-footer">
            <button
              type="button"
              className="cp-btn-secondary"
              data-tutorial-ep="web-doc-cancel"
              onClick={requestClose}
              disabled={saving}
            >
              닫기
            </button>
            <button
              type="button"
              className="cp-btn-primary"
              data-tutorial-ep="web-doc-save"
              onClick={() => void handleSave()}
              disabled={saving || !isDirty}
              title={!isDirty && !saveCompleted ? '변경 사항이 없습니다' : undefined}
            >
              {saving ? '등록 중…' : saveCompleted && !isDirty ? '등록 완료' : '등록'}
            </button>
          </div>
        </div>
      </div>
      <UnsavedChangesConfirmModal
        open={closeConfirmOpen}
        primaryLabel="등록 하기"
        primaryDisabled={saving}
        onCancel={() => setCloseConfirmOpen(false)}
        onDiscard={() => {
          setCloseConfirmOpen(false);
          setIsDirty(false);
          onClose();
        }}
        onPrimary={() => {
          setCloseConfirmOpen(false);
          void handleSave().then((ok) => {
            if (ok) onClose();
          });
        }}
      />

      {templateNameModal ? (
        <EvaluationPlanWebDocumentTemplateNameModal
          open
          mode={templateNameModal.mode}
          initialName={
            templateNameModal.mode === 'rename'
              ? templateNameModal.template.name
              : getSaveTemplateInitialName()
          }
          error={templateNameError}
          onClose={() => {
            setTemplateNameModal(null);
            setTemplateNameError(null);
          }}
          onSubmit={(name) => {
            if (templateNameModal.mode === 'save') handleSaveTemplate(name);
            else handleRenameTemplate(name);
          }}
        />
      ) : null}

      {pendingTemplateSwitch ? (
        <div
          className="cp-modal-backdrop cp-evaluation-plan-web-doc-delete-backdrop"
          role="presentation"
          onClick={() => setPendingTemplateSwitch(null)}
        >
          <div
            className="cp-modal cp-evaluation-plan-web-doc-delete-modal"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="cp-evaluation-plan-web-doc-template-switch-title"
            data-tutorial-ep="unsaved-confirm-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="cp-evaluation-plan-web-doc-template-switch-title">양식 불러오기</h3>
            <div className="cp-modal-body">
              <p className="cp-evaluation-plan-web-doc-delete-lead">
                저장하지 않은 입력 내용이 있습니다. 양식을 불러오면 저장하지 않은 텍스트는 사라집니다. 계속하시겠습니까?
              </p>
            </div>
            <div className="cp-modal-footer">
              <button type="button" className="cp-btn-secondary" onClick={() => setPendingTemplateSwitch(null)}>
                취소
              </button>
              <button
                type="button"
                className="cp-btn-primary"
                onClick={() => {
                  applyTemplate(pendingTemplateSwitch);
                  setPendingTemplateSwitch(null);
                }}
              >
                불러오기
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {pendingPlanApply ? (
        <div
          className="cp-modal-backdrop cp-evaluation-plan-web-doc-delete-backdrop"
          role="presentation"
          onClick={() => setPendingPlanApply(false)}
        >
          <div
            className="cp-modal cp-evaluation-plan-web-doc-delete-modal"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="cp-evaluation-plan-web-doc-plan-apply-title"
            data-tutorial-ep="unsaved-confirm-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="cp-evaluation-plan-web-doc-plan-apply-title">평가계획 데이터 반영</h3>
            <div className="cp-modal-body">
              <p className="cp-evaluation-plan-web-doc-delete-lead">
                입력된 내용이 있습니다. 평가계획 데이터를 반영하면 기존 입력 내용이 덮어쓰기됩니다. 계속하시겠습니까?
              </p>
            </div>
            <div className="cp-modal-footer">
              <button type="button" className="cp-btn-secondary" onClick={() => setPendingPlanApply(false)}>
                취소
              </button>
              <button
                type="button"
                className="cp-btn-primary"
                onClick={() => {
                  applyPlanData();
                  setPendingPlanApply(false);
                }}
              >
                반영
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {pendingDelete ? (
        <div
          className="cp-modal-backdrop cp-evaluation-plan-web-doc-delete-backdrop"
          role="presentation"
          onClick={() => setPendingDelete(null)}
        >
          <div
            className="cp-modal cp-evaluation-plan-web-doc-delete-modal"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="cp-evaluation-plan-web-doc-delete-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="cp-evaluation-plan-web-doc-delete-title">삭제 확인</h3>
            <div className="cp-modal-body">
              <p className="cp-evaluation-plan-web-doc-delete-lead">
                {pendingDelete.kind === 'template'
                  ? `「${pendingDelete.template.name}」 양식을 삭제하시겠습니까?`
                  : `「${pendingDelete.label}」에 입력된 내용이 있습니다. 삭제하시겠습니까?`}
              </p>
            </div>
            <div className="cp-modal-footer">
              <button type="button" className="cp-btn-secondary" onClick={() => setPendingDelete(null)}>
                취소
              </button>
              <button
                type="button"
                className="cp-btn-primary"
                onClick={() => {
                  if (pendingDelete.kind === 'template') void confirmDeleteTemplate();
                  else applyDelete(pendingDelete);
                }}
              >
                삭제
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <AutoStatusToast state={toastState} className="cp-toast-auto-over-modal" />
    </>
  );
};

export default EvaluationPlanWebDocumentEditorModal;
